import type { JobConversationViewModel } from '@/features/jobs/details/JobConversationViewModel'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import { type ConversationViewModel } from '@/features/messages/ConversationViewModel'
import { action, computed, observable, runInAction } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import type { ExternalReferenceContent } from '@/features/messages/content'
import { ChatListViewModel } from '@/features/messages/ChatListViewModel'
import { type ConversationMessageViewModel } from '@/features/messages/ConversationMessageViewModel'
import { task } from 'mobx-task'
import { ignoreAbortError } from '@/utils/abort-util'
import type { ProgressionStore } from '@/features/jobs/taxes/progression/ProgressionStore'
import type { JobProgression } from '@/features/jobs/taxes/progression/JobProgression'
import { TaxJobType } from '@/features/jobs/job-type'
import { computeProgressionFlow } from '@/features/jobs/taxes/progression/progression-content'

/**
 * A tax-job-specific implementation of {@link JobConversationViewModel}
 * with special handling for messages pertaining to job progressions.
 */
export class TaxJobConversationViewModel implements JobConversationViewModel {
  private readonly _lifecycle = new LifecycleTracker()

  readonly chatVm

  /**
   * The job progression instance, if any.
   */
  @observable accessor progression: JobProgression | null = null

  constructor(
    private readonly _progressionStore: ProgressionStore,
    private readonly _viewLatestDraft: () => unknown,
    public readonly job: SubmittedJob,
    public readonly conversationVm: ConversationViewModel,
  ) {
    this.chatVm = new ChatListViewModel<
      ConversationMessageViewModel,
      JobMessageViewModel
    >({
      messages: () => this.conversationVm.messages.slice(),
      createMessageVm: (messageVm) =>
        this._createJobMessageViewModel(messageVm),
      getMessageSender: (vm) => vm.message.sender,
      getMessageDate: (vm) => vm.message.dateCreated,
      activateMessage: (vm) => vm.activate(),
      deactivateMessage: (vm) => vm.deactivate(),
      maxDurationBetweenSameSenderMessages: 3 * 60 * 1000, // 3 minutes
    })
    this._lifecycle.manage(this.chatVm)

    // The job-specific conversation VM manages the underlying
    // actual conversation vm.
    this._lifecycle.manage(this.conversationVm)
  }

  /**
   * The number of unread messages in the conversation.
   */
  @computed get unreadCount() {
    return this.conversationVm.unreadCount
  }

  /** @inheritDoc */
  @computed get isAvailableInInfoGathering() {
    return this.job.workspace.config.jobs.enableChatInInfoGathering
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal).catch(ignoreAbortError)
  }

  /**
   * Fetches data for the conversation.
   */
  fetch = task(async (signal: AbortSignal) => {
    signal.throwIfAborted()

    const [progression] = await Promise.all([
      this._progressionStore.fetchProgression(this.job.id),
      this.conversationVm.fetchMessages(signal),
    ])
    runInAction(() => {
      this.progression = progression
    })
  })

  /**
   * Fetches the unread count.
   */
  async fetchUnreadCount(signal?: AbortSignal) {
    // The conversation will contain the unread count.
    await this.conversationVm.fetchConversation(signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Creates a wrapper message view model for the inner one.
   * This lets us match on special messages such as progression updates.
   *
   * @param messageVm
   * @private
   */
  private _createJobMessageViewModel(
    messageVm: ConversationMessageViewModel,
  ): JobMessageViewModel {
    if (
      messageVm.message.content.type === 'ExternalReference' &&
      messageVm.message.content.externalType ===
        WellKnownExternalReferenceTypes.Progression
    ) {
      return new TaxProgressionMessageViewModel(
        () => this.progression,
        this._viewLatestDraft,
        this.job,
        messageVm,
      )
    }

    return new DefaultJobMessageViewModel(messageVm)
  }
}

/**
 * Well-known external reference types for external reference content.
 */
export const WellKnownExternalReferenceTypes = {
  Progression: 'worklayer.jobs.Progression',
} as const

export type JobMessageViewModel =
  | DefaultJobMessageViewModel
  | TaxProgressionMessageViewModel

/**
 * Base class for job message view models. Acts as an interface with default
 * implementations.
 */
export abstract class JobMessageViewModelBase implements ActivationLifecycle {
  protected constructor(
    public readonly messageVm: ConversationMessageViewModel,
  ) {}

  /**
   * @inheritDoc
   * @virtual
   **/
  activate() {
    /* no-op; can be overridden */
  }

  /**
   * @inheritDoc
   * @virtual
   **/
  deactivate() {
    /* no-op; can be overridden */
  }
}

/**
 * Simple wrapper around a regular message's view model.
 * Also acts as a base class.
 */
export class DefaultJobMessageViewModel extends JobMessageViewModelBase {
  constructor(messageVm: ConversationMessageViewModel) {
    super(messageVm)
  }
}

/**
 * A message view model for a job progression step.
 */
export class TaxProgressionMessageViewModel extends JobMessageViewModelBase {
  constructor(
    private readonly _getProgression: () => JobProgression | null,
    private readonly _viewLatestDraft: () => unknown,
    private readonly _job: SubmittedJob,
    messageVm: ConversationMessageViewModel,
  ) {
    super(messageVm)
  }

  /**
   * The current progression.
   */
  get progression() {
    return this._getProgression()
  }

  /**
   * Gets the step + content for the associated progression step.
   */
  @computed get step() {
    const externalContent = this.messageVm.message
      .content as ExternalReferenceContent

    if (!this.progression || !(this._job.type instanceof TaxJobType)) {
      return null
    }

    const computedFlow = computeProgressionFlow({
      flow: this.progression.flow,
      steps: this.progression.recordedSteps.items.map((s) => s.step),
      isExtensionOnlyJob: this._job.type.isExtensionOnlyJob,
      ignoreVisibility: true,
    })

    const recordedStep = this.progression.recordedSteps.find(
      (s) => s.id === externalContent.externalId,
    )

    const computedStep = computedFlow.find((s) => s.step === recordedStep?.step)
    if (!computedStep || !recordedStep) {
      // This can happen when the progression is swapped during the extension
      // flow. There currently isn't a way to mitigate this, so we'll
      // show some generic content for now.
      return {
        step: 'Other',
        content: {
          title: 'Job update',
          description:
            'There was an update on the job, but the details are currently unavailable',
        } as const,
      } as const
    }

    const completedAt = recordedStep.response?.respondedAt

    // We only support a handful of steps.
    switch (computedStep.step) {
      case 'DraftInReview': {
        return {
          step: 'DraftInReview',
          completedAt,
          content: computedStep.content,
        } as const
      }
      case 'DraftRejected': {
        return {
          step: 'DraftRejected',
          completedAt,
          content: computedStep.content,
        } as const
      }
      case 'DraftApproved': {
        return {
          step: 'DraftApproved',
          completedAt,
          content: computedStep.content,
        } as const
      }
      // By default, we'll fall back to whatever the content says.
      default: {
        return {
          step: 'Other',
          content: computedStep.content,
        } as const
      }
    }
  }

  /**
   * Shows the latest draft.
   *
   */
  @action.bound
  viewLatestDraft() {
    this._viewLatestDraft()
  }
}
